<template>
  <div class="vm-doac" :class="'vm-lang-' + lang" @click="onContentClick">
    <div class="vm-questions">
      <template v-for="item in layout">
        <!-- ===================== Questions ===================== -->
        <div
          v-if="item.q && vis[item.q]"
          :key="'q' + item.q"
          :id="'q_' + item.q"
          class="questionBox"
          :class="boxClass(item.q)"
        >
          <div v-if="questions[item.q].header" class="questionHeader" v-html="tr(questions[item.q].header)"></div>
          <div v-if="questions[item.q].body" class="questionBody" v-html="tr(questions[item.q].body)"></div>
          <div v-if="subTextHtml(item.q)" class="questionSubText" v-html="subTextHtml(item.q)"></div>

          <!-- radio / checkbox -->
          <div v-if="isChoice(item.q)" class="questionOptions rounded">
            <ul class="hoverable">
              <li
                v-for="(opt, i) in questions[item.q].options"
                :key="opt.value"
                :class="[rowClass(item.q, i), { 'vm-disabled': isDisabled(item.q, opt.value) }]"
                class="click"
                @click="choose(item.q, opt.value)"
              >
                <table>
                  <tr>
                    <td class="vm-radio-cell">
                      <input
                        :type="questions[item.q].type"
                        :name="'q_' + item.q"
                        :id="'q_' + item.q + '_' + opt.value"
                        :value="opt.value"
                        :checked="isChecked(item.q, opt.value)"
                        :disabled="isDisabled(item.q, opt.value)"
                        @click.stop.prevent="choose(item.q, opt.value); resyncInputs()"
                      />
                    </td>
                    <td class="last">
                      <label v-html="tr(opt)"></label>
                      <input
                        v-if="opt.subtext"
                        :ref="'subtext_' + item.q + '_' + opt.value"
                        v-model="text['q_' + item.q + '_' + opt.value + '_subtext']"
                        type="text"
                        class="subtext styledTextbox"
                        :id="'q_' + item.q + '_' + opt.value + '_subtext'"
                        :disabled="!isChecked(item.q, opt.value)"
                        :placeholder="lang === 'fr' ? 'Veuillez préciser' : 'Please specify'"
                        size="20"
                        autocomplete="off"
                        @click.stop
                        @blur="onFieldBlur('q_' + item.q + '_' + opt.value + '_subtext')"
                      />
                    </td>
                  </tr>
                </table>
              </li>
            </ul>
          </div>

          <!-- number / text -->
          <div v-else-if="questions[item.q].type === 'number' || questions[item.q].type === 'textbox'" class="questionOptions rounded">
            <ul class="hoverable">
              <li v-for="(f, i) in questions[item.q].fields" :key="f.id" :class="'row' + (i + 1)">
                <div><label :for="f.id"><span v-if="tr(f)" v-html="tr(f)"></span></label></div>
                <input
                  v-model="text[f.id]"
                  type="text"
                  :inputmode="questions[item.q].type === 'number' ? 'decimal' : null"
                  class="maintext styledTextbox rounded"
                  :id="f.id"
                  :name="f.id"
                  autocomplete="off"
                  @blur="onFieldBlur(f.id)"
                />
              </li>
            </ul>
          </div>

          <!-- textarea -->
          <div v-else-if="questions[item.q].type === 'textarea'" class="questionOptions rounded">
            <textarea
              v-for="f in questions[item.q].fields"
              :key="f.id"
              v-model="text[f.id]"
              class="maintextarea"
              :id="f.id"
              :name="f.id"
              rows="4"
              @blur="onFieldBlur(f.id)"
            ></textarea>
          </div>

          <!-- date (+ result) -->
          <div v-else-if="questions[item.q].type === 'datePicker'" class="questionOptions rounded">
            <ul class="hoverable">
              <li class="row1">
                <input
                  v-model="text[questions[item.q].date]"
                  type="date"
                  class="maintext styledTextbox rounded vm-date-input"
                  :id="questions[item.q].date"
                  :name="questions[item.q].date"
                  :min="dateMin(item.q)"
                  :max="dateMax(item.q)"
                  @change="onDateChange(questions[item.q].date)"
                />
                <input
                  v-if="questions[item.q].result"
                  v-model="text[questions[item.q].result]"
                  type="text"
                  inputmode="decimal"
                  class="subtext styledTextbox vm-result-input"
                  :id="questions[item.q].result"
                  :name="questions[item.q].result"
                  :disabled="!text[questions[item.q].date] && !text[questions[item.q].result]"
                  :placeholder="lang === 'fr' ? 'Veuillez préciser' : 'Please specify'"
                  size="20"
                  autocomplete="off"
                  @blur="onFieldBlur(questions[item.q].result)"
                />
              </li>
            </ul>
          </div>

          <!-- Yes / No table -->
          <div v-else-if="questions[item.q].type === 'radioTable'" class="questionOptions rounded">
            <table class="ratingTable">
              <thead>
                <tr>
                  <th class="firstTH">&nbsp;</th>
                  <th v-for="(c, ci) in questions[item.q].cols" :key="ci" class="vm-col-head" v-html="langHtml(c)"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in questions[item.q].rows" :key="ri" :class="'row' + (ri + 1)" class="ratingRow">
                  <td class="vm-row-label"><label v-html="tr(row)"></label></td>
                  <td
                    v-for="(c, ci) in questions[item.q].cols"
                    :key="ci"
                    :class="'col' + (ci + 1)"
                    class="click vm-table-cell"
                    @click="toggleTable(item.q, ri + 1, ci + 1)"
                  >
                    <input
                      type="radio"
                      :name="'q_' + item.q + '_' + (ri + 1)"
                      :id="'q_' + item.q + '_' + (ri + 1) + '_' + (ci + 1)"
                      :value="String(ci + 1)"
                      :checked="tables[item.q][ri + 1] === String(ci + 1)"
                      :aria-label="tr(c).trim()"
                      @click.stop.prevent="toggleTable(item.q, ri + 1, ci + 1); resyncInputs()"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="footerHtml(item.q)" class="questionFooter" v-html="footerHtml(item.q)"></div>
        </div>

        <!-- ===================== Widgets (original HTML) ===================== -->
        <div
          v-else-if="item.w && widgetVisible[item.w]"
          :key="'w' + item.w"
          class="z-depth1 z-depth4 widget"
          :id="'DOAC_' + item.w + '_widget'"
        >
          <div :id="'DOAC_' + item.w + '_widgetBody'" class="vm-widget-body" v-html="widgetHtml[item.w]"></div>
        </div>
      </template>
    </div>

    <!-- ===================== Message modal (systemMessage / alert) ===================== -->
    <div v-if="modal.open" class="vm-modal-backdrop">
      <div class="vm-modal" role="dialog" aria-modal="true">
        <div class="vm-modal-header">
          <button type="button" class="vm-modal-x" aria-label="Close" @click="closeModal">&times;</button>
          <h3></h3>
        </div>
        <div class="modal-body vm-modal-body">
          <span class="english" v-html="modal.messageEn"></span><span class="french" v-html="modal.messageFr"></span>
        </div>
        <div class="vm-modal-footer">
          <button type="button" class="btn btn-primary" @click="closeModal">
            <span class="english">Close</span><span class="french">Fermer</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
 * Thrombosis Canada – DOAC Follow-up Checklist (vivomap survey 329)
 * Vue 2 port of lib/surveyStandalone/index.php?surveyID=329&questionsOnly=true
 *
 * Clinical text (questions, the creatinine-clearance box and the summary checklist) is the
 * original HTML, extracted verbatim. The logic from doac329.js / bmTools.js is ported:
 *   - QuestionToggle / Popup / WidgetObj conditions -> computed `vis` / `widgetVisible`
 *   - hideQuestions() clearing hidden answers         -> `vis` watcher
 *   - notaLock, ageWatchdog (CHADS2 "Age ≥ 75", HAS-BLED "Age ≥ 65") -> toggleCheck() / settle()
 *   - CHADS2 / HAS-BLED calculators (q_6536, q_6680)  -> calculator footers + "use value" buttons
 *   - CalcVars (Cockcroft-Gault CrCl)                 -> computed `vars`
 *   - testNumber / numeric checks / date checks       -> onFieldBlur() / onDateChange()
 *   - SummaryPatientTrigger / SpecialTriggers         -> syncDom()
 *   - makeSummaryPDF (server html2pdf)                -> `pdfHandler` prop, or print fallback
 */

var CONTENT = {"questions":{"6441":{"id":"6441","type":"number","body":{"en":"Patient Age:","fr":"Âge du patient :"},"fields":[{"id":"q_6441_1","en":"","fr":""}]},"6442":{"id":"6442","type":"number","body":{"en":"Weight:","fr":"Poids :"},"fields":[{"id":"q_6442_1","en":"Kg","fr":"Kg"},{"id":"q_6442_2","en":"Lb","fr":"Lb"}]},"6443":{"id":"6443","type":"radio","body":{"en":"Sex:","fr":"Sexe :"},"options":[{"value":"1","en":"Male","fr":"Homme"},{"value":"2","en":"Female","fr":"Femme"}]},"6444":{"id":"6444","type":"radio","body":{"en":"DOAC:","fr":"AOD :"},"options":[{"value":"1","en":"Apixaban","fr":"Apixaban"},{"value":"2","en":"Dabigatran","fr":"Dabigatran"},{"value":"3","en":"Edoxaban","fr":"Edoxaban"},{"value":"4","en":"Rivaroxaban","fr":"Rivaroxaban"}]},"6445":{"id":"6445","type":"radio","body":{"en":"Please select the Apixaban dose and frequency:","fr":"Veuillez sélectionner la dose et la fréquence d’administration de l’apixaban :"},"subText":{"en":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Dosing Tool<\/a>","fr":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Outil posologique<\/a>"},"options":[{"value":"1","en":"2.5 mg (Two times daily)","fr":"2,5 mg (2 fois par jour)"},{"value":"2","en":"5 mg (Two times daily)","fr":"5 mg (2 fois par jour)"}]},"6447":{"id":"6447","type":"radio","body":{"en":"Please select the Dabigatran dose and frequency:","fr":"Veuillez sélectionner la dose et la fréquence d’administration du dabigatran :"},"subText":{"en":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Dosing Tool<\/a>","fr":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Outil posologique<\/a>"},"options":[{"value":"1","en":"110 mg (Two times daily)","fr":"110 mg (2 fois par jour)"},{"value":"2","en":"150 mg (Two times daily)","fr":"150 mg (2 fois par jour)"}]},"6448":{"id":"6448","type":"radio","body":{"en":"Please select the Edoxaban dose and frequency:","fr":"Veuillez sélectionner la dose et la fréquence d’administration de l’edoxaban :"},"subText":{"en":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Dosing Tool<\/a>","fr":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Outil posologique<\/a>"},"options":[{"value":"1","en":"30 mg (Once daily)","fr":"30 mg (1 fois par jour)"},{"value":"2","en":"60 mg (Once daily)","fr":"60 mg (1 fois par jour)"}]},"6449":{"id":"6449","type":"radio","body":{"en":"Please select the Rivaroxaban dose and frequency:","fr":"Veuillez sélectionner la dose et la fréquence d’administration du rivaroxaban :"},"subText":{"en":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Dosing Tool<\/a>","fr":"<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">Outil posologique<\/a>"},"options":[{"value":"1","en":"15 mg (Once daily)","fr":"15 mg (1 fois par jour)"},{"value":"2","en":"20 mg (Once daily)","fr":"20 mg (1 fois par jour)"}]},"6451":{"id":"6451","type":"number","body":{"en":"CHADS<sub>2<\/sub>","fr":"CHADS<sub>2<\/sub>"},"fields":[{"id":"q_6451_1","en":"","fr":""}]},"6536":{"id":"6536","type":"checkbox","header":{"en":"CHADS2 Score for Atrial Fibrillation Stroke Risk","fr":"Score CHADS2 pour le risque d’AVC lié à la fibrillation auriculaire"},"body":{"en":"Estimates stroke risk in patients with atrial fibrillation by the CHADS2 criteria.","fr":"Estimation du risque d’AVC lié à la fibrillation auriculaire à l’aide des critères CHADS2"},"options":[{"value":"1","en":"Congestive Heart Failure history?","fr":"Antécédents d’insuffisance cardiaque congestive?"},{"value":"2","en":"Hypertension history?","fr":"Antécédents d’hypertension?"},{"value":"3","en":"Age ≥ 75?","fr":"Âge ≥ 75 ans?"},{"value":"4","en":"Diabetes Mellitus history?","fr":"Antécédents de diabète?"},{"value":"5","en":"Previous stroke or TIA?","fr":"Antécédents d’AVC ou d’AIT?"}]},"6452":{"id":"6452","type":"textblock","header":{"en":"HEALTH STATUS SINCE LAST ASSESSMENT","fr":"ÉTAT DE SANTÉ DEPUIS LA DERNIÈRE ÉVALUATION"}},"6453":{"id":"6453","type":"checkbox","body":{"en":"Please check all that apply to the patient:","fr":"Veuillez cocher toutes les cases qui s’appliquent au patient :"},"options":[{"value":"1","en":"Relevant medical problems, ED visits/hospitalizations","fr":"Affections médicales pertinentes, visites à l’urgence/hospitalisations"},{"value":"2","en":"Embolic events","fr":"Accidents emboliques"},{"value":"3","en":"None of the above","fr":"Aucune de ces réponses"}]},"6677":{"id":"6677","type":"textarea","body":{"en":"Other comments:","fr":"Autres commentaires :"},"fields":[{"id":"q_6677_1","en":"","fr":""}]},"6454":{"id":"6454","type":"textblock","header":{"en":"ADHERENCE WITH DOAC THERAPY","fr":"ADHÉSION AU TRAITEMENT AOD"}},"6455":{"id":"6455","type":"radio","body":{"en":"How many doses has the patient missed in an average week?","fr":"Combien de doses par semaine le patient a-t-il omises en moyenne?"},"options":[{"value":"1","en":"0","fr":"0"},{"value":"2","en":"1 - 2","fr":"1 - 2"},{"value":"3","en":"≥ 3","fr":"≥ 3"}]},"6456":{"id":"6456","type":"radio","body":{"en":"Any issues with taking the DOAC properly (i.e. with food/appropriate timing/etc.)?","fr":"A-t-il eu des difficultés à prendre l’AOD correctement (c.-à-d. avec de la nourriture/au moment indiqué/etc.)?"},"subText":{"en":"<a href=\"#\" data-action=\"popup1\">Drug-Food Interactions <\/a>","fr":"<a href=\"#\" data-action=\"popup1\">Interactions médicament-aliment<\/a>"},"options":[{"value":"1","en":"Yes","fr":"Oui"},{"value":"2","en":"No","fr":"Non"}]},"6535":{"id":"6535","type":"popup","body":{"en":"<p><strong>Apixaban<\/strong><\/p>\r\n<p>Apixaban can be taken with or without food.<br />\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/ApixabanProductMonograph.pdf\" target=\"_blank\">See apixaban product monograph.<\/a><\/p>\r\n<p><strong>Dabigatran<\/strong><\/p>\r\n<p>Food does not affect the bioavailability of dabigatran but delays the time-to-peak plasma concentrations by 2 hours.<br />\r\nDabigatran should be taken with a full glass of water to facilitate delivery to the stomach. Dabigatran may be taken with food, or on an empty stomach. Patients experiencing dyspepsia should take dabigatran with meals.<br />\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/DabigatranProductMonograph.pdf\" target=\"_blank\">See dabigatran product monograph.<\/a><\/p>\r\n<p><strong>Edoxaban<\/strong><\/p>\r\n<p>Edoxaban can be taken with or without food.<br>\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/EdoxabanProductMonograph.pdf\" target=\"_blank\">See edoxaban product monograph.<\/a><\/p>\r\n<p><strong>Rivaroxaban<\/strong><\/p>\r\n<p>Rivaroxaban 10 mg may be taken with or without food. Rivaroxaban 15 mg and 20 mg should be taken with food. Bioavailability is reduced by a mean of 39% if not taken with food.<br />\r\nGrapefruit juice is a moderate CYP 3A4 inhibitor. Therefore, an increase in rivaroxaban exposure following grapefruit juice consumption is not expected to be clinically relevant.<br />\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/RivaroxabanProductMonograph.pdf\" target=\"_blank\">See rivaroxaban product monograph.<\/a><\/p>","fr":"<p><strong>Apixaban<\/strong><\/p>\r\n<p>Apixaban can be taken with or without food.<br />\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/ApixabanProductMonograph.pdf\" target=\"_blank\">See apixaban product monograph.<\/a><\/p>\r\n<p><strong>Dabigatran<\/strong><\/p>\r\n<p>Food does not affect the bioavailability of dabigatran but delays the time-to-peak plasma concentrations by 2 hours.<br />\r\nDabigatran should be taken with a full glass of water to facilitate delivery to the stomach. Dabigatran may be taken with food, or on an empty stomach. Patients experiencing dyspepsia should take dabigatran with meals.<br />\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/DabigatranProductMonograph.pdf\" target=\"_blank\">See dabigatran product monograph.<\/a><\/p>\r\n<p><strong>Edoxaban<\/strong><\/p>\r\n<p>Edoxaban can be taken with or without food.<br>\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/EdoxabanProductMonograph.pdf\" target=\"_blank\">See edoxaban product monograph.<\/a><\/p>\r\n<p><strong>Rivaroxaban<\/strong><\/p>\r\n<p>Rivaroxaban 10 mg may be taken with or without food. Rivaroxaban 15 mg and 20 mg should be taken with food. Bioavailability is reduced by a mean of 39% if not taken with food.<br />\r\nGrapefruit juice is a moderate CYP 3A4 inhibitor. Therefore, an increase in rivaroxaban exposure following grapefruit juice consumption is not expected to be clinically relevant.<br />\r\n<a href=\"%%ASSET_BASE%%/assets/programs/116/RivaroxabanProductMonograph.pdf\" target=\"_blank\">See rivaroxaban product monograph.<\/a><\/p>"}},"6457":{"id":"6457","type":"textblock","header":{"en":"BLEEDING RISK ASSESSMENT","fr":"ÉVALUATION DU RISQUE DE SAIGNEMENT"}},"6678":{"id":"6678","type":"number","body":{"en":"HAS-BLED:","fr":"HAS-BLED:"},"fields":[{"id":"q_6678_1","en":"","fr":""}]},"6680":{"id":"6680","type":"checkbox","header":{"en":"HAS-BLED Score for Major Bleeding Risk","fr":"Score HAS-BLED du risque d’hémorragie grave"},"body":{"en":"Estimates risk of major bleeding for patients on anticoagulation to help determine risk-benefit in atrial fibrillation care.","fr":"Estimation du risque d’hémorragie grave des patients sous anticoagulothérapie, permettant de déterminer le rapport risques/avantages du traitement de la fibrillation auriculaire ."},"options":[{"value":"1","en":"Hypertension History (uncontrolled, &gt;160 mmHg systolic)","fr":"Antécédents d’hypertension (non maîtrisée, TA systolique &gt; 160 mm Hg)"},{"value":"2","en":"Renal Disease (Dialysis, transpant, Cr &gt; 2.6 mg/dl or &gt; 200 µmol/L)","fr":"Néphropathie (dialyse, greffe, Cr &gt; 2,6 mg/dL ou &gt; 200 µmol/L)"},{"value":"3","en":"Liver Disease (Cirrhosis, Bilirubin &gt; 2x Normal, AST/ALT/AP &gt; 3x Normal)","fr":"Maladie du foie (cirrhose, taux de bilirubine &gt; 2 fois la normale, AST/ALT/AP &gt; 3 fois la normale)"},{"value":"4","en":"Stroke History","fr":"Antécédents d’AVC"},{"value":"5","en":"Prior Major Bleeding or Predisposition to Bleeding","fr":"Antécédents d’hémorragie grave ou prédisposition aux saignements"},{"value":"6","en":"Labile INR (Unstable/high INRs)","fr":"Fluctuations du RIN (RIN instables/élevés)"},{"value":"7","en":"Age ≥ 65","fr":"Âge ≥ 65 ans"},{"value":"8","en":"Medication Usage Predisposing to Bleeding (Antiplatelet agents, NSAIDS)","fr":"Prise de médicaments prédisposant aux hémorragies (antiplaquettaires, AINS)"},{"value":"9","en":"Alcohol Usage History","fr":"Antécédents de consommation d’alcool"}]},"6458":{"id":"6458","type":"checkbox","body":{"en":"Please check all that apply. <i> NB: a YES to any of the following requires individualized assessment and does not imply that DOAC should be discontinued<\/i>","fr":"Veuillez cocher tout ce qui s’applique.  <i>Remarque : Une réponse « OUI » à l’une de ces questions exige une évaluation personnalisée et ne signifie pas qu’il faut arrêter l’AOD. <\/i>"},"options":[{"value":"1","en":"Signs / symptoms of GI bleeding","fr":"Signes/symptômes d’hémorragie GI"},{"value":"2","en":"Signs / symptoms of other bleeding","fr":"Signes/symptômes d’une autre hémorragie","subtext":true},{"value":"3","en":"Drop in hemoglobin or new anemia","fr":"Baisse du taux d’hémoglobine ou apparition de l’anémie"},{"value":"4","en":"Hypotension with syncope/falls","fr":"Hypotension avec syncope/chutes"},{"value":"5","en":"None of the above","fr":"Aucune de ces réponses"}]},"6459":{"id":"6459","type":"datePicker","body":{"en":"When was hemoglobin last measured and what was the result (g/dL)?","fr":"Quand le taux d’hémoglobine a-t-il été mesuré pour la dernière fois et quel était le résultat (g/dL)?"},"date":"q_6459_1","result":"q_6459_1_subtext"},"6460":{"id":"6460","type":"textblock","header":{"en":"CREATININE CLEARANCE / RENAL FUNCTION","fr":"CLAIRANCE DE LA CRÉATININE/FONCTIONNEMENT RÉNAL"}},"6462":{"id":"6462","type":"datePicker","body":{"en":"When was eGFR last measured and what was the result?","fr":"Quand le TFGe a-t-il été mesuré pour la dernière fois et quel était le résultat?"},"date":"q_6462_1","result":"q_6462_1_subtext"},"6461":{"id":"6461","type":"datePicker","body":{"en":"If eGFR is less than 50mL/min, it is recommended to calculate creatinine clearance.  When was serum creatinine last measured and what was the result (µmol/L)","fr":"Si le TFGe est inférieur à 50 mL/min, il est recommandé de calculer la clairance de la créatinine. Quand le taux de créatinine sérique a-t-il été mesuré pour la dernière fois et quel était le résultat (µmol/L)?"},"date":"q_6461_1","result":"q_6461_1_subtext"},"6463":{"id":"6463","type":"radio","body":{"en":"Any recent dehydrating illness or medications added/changed (i.e. diuretics, SGLT2 receptor antagonists)?","fr":"Y a-t-il des antécédents récents d’une affection causant une déshydratation ou d’ajout/de changements de médicaments (p. ex., diurétiques, inhibiteurs du SGLT-2)?"},"options":[{"value":"1","en":"Yes","fr":"Oui"},{"value":"2","en":"No","fr":"Non"}]},"6679":{"id":"6679","type":"textarea","body":{"en":"Other comments:","fr":"Autres commentaires :"},"fields":[{"id":"q_6679_1","en":"","fr":""}]},"6464":{"id":"6464","type":"textblock","header":{"en":"DRUG INTERACTIONS (review all concomitant medications)","fr":"INTERACTIONS MÉDICAMENTEUSES (passer en revue tous les médicaments concomitants)"}},"6465":{"id":"6465","type":"checkbox","body":{"en":"Please check all that apply:","fr":"Veuillez cocher tout ce qui s’applique :"},"options":[{"value":"1","en":"ASA / other antiplatelets","fr":"AAS/autres antiplaquettaires"},{"value":"2","en":"NSAID","fr":"AINS"},{"value":"3","en":"Other drug interactions","fr":"Autres interactions médicamenteuses"},{"value":"4","en":"None of the above","fr":"Aucune de ces réponses"}]},"6466":{"id":"6466","type":"textblock","header":{"en":"EXAMINATION","fr":"EXAMEN"}},"6468":{"id":"6468","type":"number","body":{"en":"Actual BP","fr":"Tension artérielle mesurée"},"fields":[{"id":"q_6468_1","en":"Systolic","fr":"Systolique"},{"id":"q_6468_2","en":"Diastolic","fr":"Diastolique"}]},"6467":{"id":"6467","type":"radio","body":{"en":"Patient's blood pressure is:","fr":"État de la tension artérielle du patient :"},"options":[{"value":"1","en":"Within target","fr":"Dans l’intervalle des valeurs cibles"},{"value":"2","en":"High","fr":"Élevée"},{"value":"3","en":"Low","fr":"Faible"}]},"6469":{"id":"6469","type":"radio","body":{"en":"Does patient need referral for gait assessment/walking aids for falls prevention?","fr":"Le patient a-t-il besoin d’être orienté pour une évaluation de la démarche/de ses besoins d’aide à la marche pour la prévention des chutes?"},"options":[{"value":"1","en":"Yes","fr":"Oui"},{"value":"2","en":"No","fr":"Non"}]},"6470":{"id":"6470","type":"textblock","header":{"en":"FINAL ASSESSMENT & RECOMMENDATIONS","fr":"DERNIÈRE ÉVALUATION ET RECOMMANDATIONS"}},"6471":{"id":"6471","type":"radioTable","body":{"en":"Please complete the following:","fr":"Veuillez cocher ce qui s’applique :"},"cols":[{"en":"Yes","fr":"Oui"},{"en":"No","fr":"Non"}],"rows":[{"en":"Overall patient appears stable from the anticoagulant standpoint; benefits of continued anticoagulant therapy outweigh risks; Recommend continue current anticoagulant therapy.","fr":"Dans l’ensemble, l’état du patient semble stable dans le contexte de l’anticoagulation; les avantages de la poursuite de l’anticoagulothérapie l’emportent sur les risques. Recommander la poursuite du traitement anticoagulant en cours."},{"en":"Dose verified and is appropriate for patient’s age/weight/renal function/health status <a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">(Dosing Tool)<\/a>","fr":"Dose vérifiée et adaptée à l’âge, au poids, au fonctionnement rénal et à l’état de santé du patient<a href=\"http://thrombosiscanada.ca/?page_id=502\" target=\"_blank\">(Outil posologique)<\/a>"},{"en":"Any changes to current therapy needed?","fr":"Faut-il modifier le traitement en cours?"}]},"6472":{"id":"6472","type":"textbox","body":{"en":"Details of therapy change:","fr":"Précisions concernant les modifications du traitement :"},"fields":[{"id":"q_6472_1","en":"","fr":""}]},"6473":{"id":"6473","type":"textblock","header":{"en":"PATIENT EDUCATION & COUNSELING","fr":"INFORMATION ET CONSEILS DESTINÉS AU PATIENT"}},"6474":{"id":"6474","type":"radioTable","body":{"en":"I have counselled about the following:","fr":"Veuillez cocher ce qui s’applique :"},"cols":[{"en":"Yes","fr":"Oui"},{"en":"No","fr":"Non"}],"rows":[{"en":"The rationale for continued DOAC therapy","fr":"Raisons de poursuivre le traitement AOD"},{"en":"The potential for minor, major or life-threatening bleeding","fr":"Risque d’hémorragie mineure, majeure ou menaçant le pronostic vital"},{"en":"Dosing instructions, adherence, risks of non-adherence, handling missed doses","fr":"Directives posologiques, observance, risques de non-observance, marche à suivre en cas d’omission de doses"},{"en":"Avoiding OTC ASA  &amp; NSAIDs &amp; minimizing EtOH to reduce bleeding risks","fr":"Nécessité d’éviter l’AAS et les AINS en vente libre et de réduire au minimum la consommation d’alcool pour limiter les risques hémorragiques"}]},"6475":{"id":"6475","type":"datePicker","body":{"en":"Next follow-up date:","fr":"Date du prochain suivi :"},"date":"q_6475_1"},"6476":{"id":"6476","type":"datePicker","body":{"en":"Next bloodwork:","fr":"Prochaines analyses de sang :"},"date":"q_6476_1"}},"widgets":{"2":"<div style=\"position:relative;\"> <span class=\"english\"> <p><strong>Creatinine Clearance (Cockcroft-Gault Equation)<\/strong><\/p> <p>Based on the information provided, the calculated creatinine clearance according to the Cockgroft-Gault equation is calculation per <span class=\"CrCl value\"><\/span><\/p> <\/span> <span class=\"french\"> <p><strong>Clairance de la créatinine (équation de Cockcroft-Gault)<\/strong><\/p> <p>D’après les renseignements fournis, la clairance de la créatinine calculée à l’aide de l’équation de Cockgroft-Gault est de <span class=\"CrCl value\"><\/span><\/p> <\/span> <\/div>","Summary":"<div style=\"position:relative;\">\n<span class=\"english wrapper\">\n<div class=\"doacSummary\">\n<table border=\"0\" class=\"summary\">\n<tbody><tr class=\"main_header\">\n<td colspan=\"2\"><center><h3>Direct Oral Anticoagulant (DOAC) Follow-up Checklist<\/h3><\/center><\/td>\n<\/tr>\n<tr class=\"sub_header\">\n<td colspan=\"2\">Date: <span class=\"vm-date\"><\/span><\/td>\n<\/tr>\n<tr class=\"sub_header\">\n<td colspan=\"2\">Summary of Patient Profile<\/td>\n<\/tr>\n<tr class=\"\">\n<td>Patient Name:<\/td>\n<td style=\"width:50%;\"><\/td>\n<\/tr>\n<tr class=\"Q1 line\" style=\"display: none;\">\n<td>Age:<\/td>\n<td class=\"Q1 value\" style=\"width:50%;\"><\/td>\n<\/tr>\n<tr class=\"Q2 line\" style=\"display: none;\">\n<td>Weight:<\/td>\n<td><span class=\"Q2 value\"><\/span> kg<\/td>\n<\/tr>\n<tr class=\"Q3 line\" style=\"display: none;\">\n<td>Sex:<\/td>\n<td class=\"Q3 value\"><\/td>\n<\/tr>\n<tr class=\"Q4 line\" style=\"display: none;\">\n<td>DOAC:<\/td>\n<td class=\"Q4 value\"><\/td>\n<\/tr>\n<tr class=\"Q4_135 line\" style=\"display: none;\">\n<td>Dose and Frequency:<\/td>\n<td>\n<span class=\"Q4_1 value\"><\/span>\n<span class=\"Q4_3 value\"><\/span>\n<span class=\"Q4_4 value\"><\/span>\n<span class=\"Q4_5 value\"><\/span>\n<\/td>\n<\/tr>\n<tr class=\"Q5 line\" style=\"display: none;\">\n<td>CHADS<sub>2<\/sub>:<\/td>\n<td class=\"Q5 value\"><\/td>\n<\/tr>\n<tr class=\"Q8_5 line\" style=\"display: none;\">\n<td>HAS-BLED:<\/td>\n<td class=\"Q8_5 value\"><\/td>\n<\/tr>\n<tr class=\"Q6Q6_1 line\" style=\"display: none;\">\n<td><strong>HEALTH STATUS SINCE LAST ASSESSMENT<\/strong><\/td>\n<td>\n<ul class=\"\">\n<li class=\"Q6A trigger\" style=\"display: none;\">Relevant medical problems, ED visits/hospitalizations<\/li>\n<li class=\"Q6B trigger\" style=\"display: none;\">Embolic events<\/li>\n<li class=\"Q6_1 trigger value\" style=\"display: none;\"><\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q7Q8A line\" style=\"display: none;\">\n<td><strong>ADHERENCE WITH DOAC THERAPY<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q7 trigger\" style=\"display: none;\"><span class=\"Q7 value\"><\/span> missed doses in an average week<\/li>\n<li class=\"Q8A trigger\" style=\"display: none;\">Issues with taking the DOAC properly (i.e. with food/appropriate timing/etc)<\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q9Q9_1 line\" style=\"display: none;\">\n<td><strong>BLEEDING RISK: <br/><em>Any of the noted factors requires individualized assessment and does not imply that DOAC should be discontinued<\/em><\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q9A value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9B value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9C value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9D value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9E trigger\" style=\"display: none;\">None<\/li>\n<li class=\"Q9_1 line trigger\" style=\"display: none;\">Latest hemoglobin <span class=\"Q9_1 value\"><\/span> (g/dL)<\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q10Q11Q12A line\" style=\"display: none;\">\n<td><strong>CREATININE CLEARANCE / RENAL FUNCTION<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q10 line\" style=\"display: none;\">Latest serum creatinine (µmol/L): <span class=\"CMG value\"><\/span><\/li>\n<li class=\"Q11X trigger\" style=\"display: none;\">Latest eGFR: <span class=\"Q11X value\"><\/span><\/li>\n<li class=\"EGFRLT50 trigger\" style=\"display: none;\">Calculated creatinine clearance if eGFR &lt; 50 ml/min: <span class=\"CrCl value\"><\/span><\/li>\n<li class=\"Q12A trigger\" style=\"display: none;\">Recent dehydrating illness or medications added/changed (i.e. diuretics).<\/li>\n<li class=\"Q12_1 trigger value\" style=\"display: none;\"><\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q13X line\" style=\"display: none;\">\n<td><strong>DRUG INTERACTIONS (review all concomitant medications)<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q13 value\"><\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q14Q15 line\" style=\"display: none;\">\n<td><strong>EXAMINATION<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q14 line\" style=\"display: none;\">Actual BP: Systolic: <span class=\"Q14A value\"><\/span>, Diastolic: <span class=\"Q14B value\"><\/span><\/li>\n<li class=\"Q14_1 trigger\" style=\"display: none;\">Patient's blood pressure is: <span class=\"Q14_1 value\"><\/span><\/li>\n<li class=\"Q15A trigger\" style=\"display: none;\">Needs referral for gait assessment/walking aids for falls prevention.<\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q16 line\" style=\"display: none;\">\n<td><strong>FINAL ASSESSMENT &amp; RECOMMENDATIONS<\/strong><\/td>\n<td>\n<div class=\"Q16A trigger\" style=\"display: none;\">\nOverall patient appears stable from the anticoagulant standpoint; benefits of continued anticoagulant therapy outweigh risks; Recommend continue current anticoagulant therapy: <strong><span class=\"Q16A value\"><\/span><\/strong>\n<\/div><br/>\n<div class=\"Q16B trigger\" style=\"display: none;\">\nDose verified and is appropriate for patient’s age/weight/renal function/health status: <strong><span class=\"Q16B value\"><\/span><\/strong>\n<\/div><br/>\n<div class=\"Q16C trigger\" style=\"display: none;\">\nAny changes to current therapy needed? <strong><span class=\"Q16C value\"><\/span><\/strong>\n<\/div><br/>\n<div class=\"Q16_1 trigger\" style=\"display: none;\">\nDetails of therapy change: <span class=\"Q16_1 value\"><\/span>\n<\/div><br/>\n<\/td>\n<\/tr>\n<tr class=\"Q17Q18Q19 line\" style=\"display: none;\">\n<td><strong>PATIENT EDUCATION &amp; COUNSELING<\/strong><\/td>\n<td>\n<span class=\"Q17X trigger\" style=\"display: none;\">I have counselled about the following: <\/span>\n<ul class=\"Q17X trigger\" style=\"display: none;\">\n<li class=\"Q17A_C1\" style=\"display: none;\">The rationale for continued DOAC therapy<\/li>\n<li class=\"Q17B_C1\" style=\"display: none;\">The potential for minor, major or life-threatening bleeding<\/li>\n<li class=\"Q17C_C1\" style=\"display: none;\">Dosing instructions, adherence, risks of non-adherence, handling missed doses<\/li>\n<li class=\"Q17D_C1\" style=\"display: none;\">Avoiding OTC ASA &amp; NSAIDs &amp; minimizing EtOH to reduce bleeding risks<\/li>\n<\/ul>\n<br/>\n<div class=\"Q18 trigger\" style=\"display: none;\">Next follow-up date: <span class=\"Q18 value\"><\/span><\/div>\n<br/>\n<div class=\"Q19 trigger\" style=\"display: none;\">Next bloodwork: <span class=\"Q19 value\"><\/span><\/div>\n<br/>\n<\/td>\n<\/tr>\n<tr class=\"sub_header\">\n<td colspan=\"2\">For more information<\/td>\n<\/tr>\n<tr>\n<td colspan=\"2\">Visit the Thrombosis Canada website at <a href=\"http://www.thrombosiscanada.ca\" target=\"_blank\">www.thrombosiscanada.ca<\/a><\/td>\n<\/tr>\n<tr>\n<td colspan=\"2\">\n<strong><u>Disclaimer: <\/u><\/strong><br/>\n<em>These general recommendations do not replace clinical judgement. Physicians must consider relative risks and benefits in each patient in applying these recommendations.<\/em>\n<\/td>\n<\/tr>\n<\/tbody><\/table>\n<\/div>\n<br/><br/>\n<center>\n<a class=\"click_button\" data-action=\"pdf\">\n<span class=\"english\">Click here to generate a PDF of this summary.<\/span>\n<!-- <span class=\"french\">Cliquez ici pour obtenir une version PDF de ce résumé.<\/span> -->\n<\/a>\n<a class=\"button btn btn-info\" href=\"\" id=\"pdf_link\" style=\"display:none\" target=\"_blank\">\n<span class=\"english\">Click here to download a PDF of this summary.<\/span>\n<!-- <span class=\"french\">Cliquez ici pour télécharger une version PDF de ce résumé.<\/span> -->\n<\/a>\n<\/center>\n<\/span>\n<span class=\"french wrapper\">\n<div class=\"doacSummary\">\n<table border=\"0\" class=\"summary\">\n<tbody><tr class=\"main_header\">\n<td colspan=\"2\"><center><h3>Liste de contrôle de suivi des anticoagulants par voie orale directs (AOD)<\/h3><\/center><\/td>\n<\/tr>\n<tr class=\"sub_header\">\n<td colspan=\"2\">Date: <span class=\"vm-date\"><\/span><\/td>\n<\/tr>\n<tr class=\"sub_header\">\n<td colspan=\"2\">Résumé du profil du patient<\/td>\n<\/tr>\n<tr class=\"\">\n<td>Nom du patient:<\/td>\n<td style=\"width:50%;\"><\/td>\n<\/tr>\n<tr class=\"Q1 line\" style=\"display: none;\">\n<td>Âge :<\/td>\n<td class=\"Q1 value\" style=\"width:50%;\"><\/td>\n<\/tr>\n<tr class=\"Q2 line\" style=\"display: none;\">\n<td>Poids :<\/td>\n<td><span class=\"Q2 value\"><\/span> kg<\/td>\n<\/tr>\n<tr class=\"Q3 line\" style=\"display: none;\">\n<td>Sexe :<\/td>\n<td class=\"Q3 value\"><\/td>\n<\/tr>\n<tr class=\"Q4 line\" style=\"display: none;\">\n<td>AOD :<\/td>\n<td class=\"Q4 value\"><\/td>\n<\/tr>\n<tr class=\"Q4_135 line\" style=\"display: none;\">\n<td>Dose et fréquence :<\/td>\n<td>\n<span class=\"Q4_1 value\"><\/span>\n<span class=\"Q4_3 value\"><\/span>\n<span class=\"Q4_4 value\"><\/span>\n<span class=\"Q4_5 value\"><\/span>\n<\/td>\n<\/tr>\n<tr class=\"Q5 line\" style=\"display: none;\">\n<td>CHADS<sub>2<\/sub>:<\/td>\n<td class=\"Q5 value\"><\/td>\n<\/tr>\n<tr class=\"Q8_5 line\" style=\"display: none;\">\n<td>HAS-BLED:<\/td>\n<td class=\"Q8_5 value\"><\/td>\n<\/tr>\n<tr class=\"Q6Q6_1 line\" style=\"display: none;\">\n<td><strong>ÉTAT DE SANTÉ DEPUIS LA DERNIÈRE ÉVALUATION<\/strong><\/td>\n<td>\n<ul class=\"\">\n<li class=\"Q6A trigger\" style=\"display: none;\">Problèmes médicaux pertinents, visites/hospitalisations à l’urgence<\/li>\n<li class=\"Q6B trigger\" style=\"display: none;\">Événements emboliques<\/li>\n<li class=\"Q6_1 trigger value\" style=\"display: none;\"><\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q7Q8A line\" style=\"display: none;\">\n<td><strong>ADHÉSION AU TRAITEMENT AOD<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q7 trigger\" style=\"display: none;\"><span class=\"Q7 value\"><\/span> doses oubliées au cours d'une semaine moyenne<\/li>\n<li class=\"Q8A trigger\" style=\"display: none;\">Problèmes liés à la prise correcte de l’AOD (c'est-à-dire avec de la nourriture/un moment approprié/etc.)<\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q9Q9_1 line\" style=\"display: none;\">\n<td><strong>RISQUE DE SAIGNEMENT: <br/><em>Chacun des facteurs mentionnés nécessite une évaluation individualisée et n'implique pas que l'AOD doit être abandonné.<\/em><\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q9A value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9B value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9C value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9D value trigger\" style=\"display: none;\"><\/li>\n<li class=\"Q9E trigger\" style=\"display: none;\">Aucun<\/li>\n<li class=\"Q9_1 line trigger\" style=\"display: none;\">Dernière hémoglobine <span class=\"Q9_1 value\"><\/span> (g/dL)<\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q10Q11Q12A line\" style=\"display: none;\">\n<td><strong>CLAIRANCE DE LA CRÉATININE/FONCTIONNEMENT RÉNAL<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q10 line\" style=\"display: none;\">Dernière créatinine sérique (µmol/L) : <span class=\"CMG value\"><\/span><\/li>\n<li class=\"Q11X trigger\" style=\"display: none;\">Dernier DFGe : <span class=\"Q11X value\"><\/span><\/li>\n<li class=\"EGFRLT50 trigger\" style=\"display: none;\">Clairance de la créatinine calculée si DFGe &lt; 50 ml/min : <span class=\"CrCl value\"><\/span><\/li>\n<li class=\"Q12A trigger\" style=\"display: none;\">Maladie de déshydratation récente ou médicaments ajoutés/modifiés (c.-à-d. Diurétiques).<\/li>\n<li class=\"Q12_1 trigger value\" style=\"display: none;\"><\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q13X line\" style=\"display: none;\">\n<td><strong>INTERACTIONS MÉDICAMENTEUSES (passer en revue tous les médicaments concomitants)<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q13 value\"><\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q14Q15 line\" style=\"display: none;\">\n<td><strong>EXAMEN<\/strong><\/td>\n<td>\n<ul>\n<li class=\"Q14 line\" style=\"display: none;\">Tension artérielle mesurée: Systolique: <span class=\"Q14A value\"><\/span>, Diastolique: <span class=\"Q14B value\"><\/span><\/li>\n<li class=\"Q14_1 trigger\" style=\"display: none;\">La tension artérielle du patient est : <span class=\"Q14_1 value\"><\/span><\/li>\n<li class=\"Q15A trigger\" style=\"display: none;\">Nécessite une référence pour une évaluation de la démarche/des aides à la marche pour la prévention des chutes.<\/li>\n<\/ul>\n<\/td>\n<\/tr>\n<tr class=\"Q16 line\" style=\"display: none;\">\n<td><strong>DERNIÈRE ÉVALUATION ET RECOMMANDATIONS<\/strong><\/td>\n<td>\n<div class=\"Q16A trigger\" style=\"display: none;\">\nEn générale, le patient semble stable du point de vue anticoagulant ; les avantages de la poursuite du traitement anticoagulant l'emportent sur les risques ; Recommander de poursuivre le traitement anticoagulant actuel : <strong><span class=\"Q16A value\"><\/span><\/strong>\n<\/div><br/>\n<div class=\"Q16B trigger\" style=\"display: none;\">\nDose vérifiée et approprié à l’âge/au poids/à la fonction rénale/à l’état de santé du patient : <strong><span class=\"Q16B value\"><\/span><\/strong>\n<\/div><br/>\n<div class=\"Q16C trigger\" style=\"display: none;\">\nDes changements au traitement actuel sont-ils nécessaires ? <strong><span class=\"Q16C value\"><\/span><\/strong>\n<\/div><br/>\n<div class=\"Q16_1 trigger\" style=\"display: none;\">\nDétails du changement thérapeutique : <span class=\"Q16_1 value\"><\/span>\n<\/div><br/>\n<\/td>\n<\/tr>\n<tr class=\"Q17Q18Q19 line\" style=\"display: none;\">\n<td><strong>INFORMATION ET CONSEILS DESTINÉS AU PATIENT<\/strong><\/td>\n<td>\n<span class=\"Q17X trigger\" style=\"display: none;\">Veuillez cocher ce qui s’applique : <\/span>\n<ul class=\"Q17X trigger\" style=\"display: none;\">\n<li class=\"Q17A_C1\" style=\"display: none;\">Raisons de poursuivre le traitement AOD<\/li>\n<li class=\"Q17B_C1\" style=\"display: none;\">Risque d’hémorragie mineure, majeure ou menaçant le pronostic vital<\/li>\n<li class=\"Q17C_C1\" style=\"display: none;\">Directives posologiques, observance, risques de non-observance, marche à suivre en cas d’omission de doses<\/li>\n<li class=\"Q17D_C1\" style=\"display: none;\">Nécessité d’éviter l’AAS et les AINS en vente libre et de réduire au minimum la consommation d’alcool pour limiter les risques hémorragiques<\/li>\n<\/ul>\n<br/>\n<div class=\"Q18 trigger\" style=\"display: none;\">Date du prochain suivi : <span class=\"Q18 value\"><\/span><\/div>\n<br/>\n<div class=\"Q19 trigger\" style=\"display: none;\">Prochaines analyses de sang : <span class=\"Q19 value\"><\/span><\/div>\n<br/>\n<\/td>\n<\/tr>\n<tr class=\"sub_header\">\n<td colspan=\"2\">Pour de plus amples renseignements<\/td>\n<\/tr>\n<tr>\n<td colspan=\"2\">Visitez le site Web de Thrombose Canada au <a href=\"http://www.thrombosiscanada.ca\" target=\"_blank\">www.thrombosiscanada.ca<\/a><\/td>\n<\/tr>\n<tr>\n<td colspan=\"2\">\n<strong><u>Avis de non-responsabilité :<\/u><\/strong><br/>\n<em>Ces recommandations générales ne doivent pas se substituer au jugement du clinicien. Les médecins doivent pondérer les risques et les avantages relatifs de l’application de ces recommandations chez chaque patient.<\/em>\n<\/td>\n<\/tr>\n<\/tbody><\/table>\n<\/div>\n<br/><br/>\n<center>\n<a class=\"click_button\" data-action=\"pdf\">\n<span class=\"french\">Cliquez ici pour obtenir une version PDF de ce résumé.<\/span>\n<\/a>\n<a class=\"button btn btn-info\" href=\"\" id=\"pdf_link\" style=\"display:none\" target=\"_blank\">\n<span class=\"french\">Cliquez ici pour télécharger une version PDF de ce résumé.<\/span>\n<\/a>\n<\/center>\n<\/span>\n<\/div>"}}

var LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P']
var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December']

// Render order (as in the original page). DOAC_2 follows q_6461, the summary comes last.
var LAYOUT = [
  { q: '6441' }, { q: '6442' }, { q: '6443' }, { q: '6444' }, { q: '6445' }, { q: '6447' }, { q: '6448' },
  { q: '6449' }, { q: '6451' }, { q: '6536' }, { q: '6452' }, { q: '6453' }, { q: '6677' }, { q: '6454' },
  { q: '6455' }, { q: '6456' }, { q: '6535' }, { q: '6457' }, { q: '6678' }, { q: '6680' }, { q: '6458' },
  { q: '6459' }, { q: '6460' }, { q: '6462' }, { q: '6461' }, { w: '2' }, { q: '6463' }, { q: '6679' },
  { q: '6464' }, { q: '6465' }, { q: '6466' }, { q: '6468' }, { q: '6467' }, { q: '6469' }, { q: '6470' },
  { q: '6471' }, { q: '6472' }, { q: '6473' }, { q: '6474' }, { q: '6475' }, { q: '6476' }, { w: 'Summary' }
]

var RADIOS = ['6443', '6444', '6445', '6447', '6448', '6449', '6455', '6456', '6463', '6467', '6469']
var CHECKS = ['6536', '6453', '6680', '6458', '6465']
// "None of the above" options that lock the rest of their checklist (notaLock)
var NOTA = { 6453: '3', 6458: '5', 6465: '4' }
// DOAC (q_6444) -> dose question, and the drug-interaction chart linked from Q13
var DOSE_Q = { 1: '6445', 2: '6447', 3: '6448', 4: '6449' }
var INTERACTION_CHART = { 1: 'Q3A', 2: 'Q3B', 3: 'Q3C', 4: 'Q3D' }

var TEXT_FIELDS = ['q_6441_1', 'q_6442_1', 'q_6442_2', 'q_6451_1', 'q_6678_1', 'q_6677_1', 'q_6679_1',
  'q_6468_1', 'q_6468_2', 'q_6472_1', 'q_6458_2_subtext',
  'q_6459_1', 'q_6459_1_subtext', 'q_6461_1', 'q_6461_1_subtext', 'q_6462_1', 'q_6462_1_subtext',
  'q_6475_1', 'q_6476_1']

function isNumeric (n) {
  // eslint-disable-next-line eqeqeq
  return n == parseFloat(n)
}
function escapeHtml (s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
function multiline (s) {
  return escapeHtml(s).replace(/\r?\n/g, '<br>')
}
function langSpans (en, fr) {
  return '<span class="english">' + en + '</span><span class="french">' + (fr === undefined ? en : fr) + '</span>'
}
function has (arr, v) { return !!arr && arr.indexOf(String(v)) !== -1 }
function pad (n) { return (n < 10 ? '0' : '') + n }
function isoToday () {
  var d = new Date()
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}
// <input type="date"> value (YYYY-MM-DD) -> the MM/DD/YYYY the original datepicker produced
function displayDate (iso) {
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  return m ? m[2] + '/' + m[3] + '/' + m[1] : (iso || '')
}
function blank (keys) {
  var o = {}
  keys.forEach(function (k) { o[k] = '' })
  return o
}
function blankRadio () { var o = {}; RADIOS.forEach(function (k) { o[k] = null }); return o }
function blankChecks () { var o = {}; CHECKS.forEach(function (k) { o[k] = [] }); return o }
function blankTables () {
  return { 6471: { 1: null, 2: null, 3: null }, 6474: { 1: null, 2: null, 3: null, 4: null } }
}

var displayCache = {
  SPAN: 'inline', A: 'inline', B: 'inline', STRONG: 'inline', EM: 'inline', I: 'inline', U: 'inline',
  MARK: 'inline', SUP: 'inline', SUB: 'inline', SMALL: 'inline', LABEL: 'inline', IMG: 'inline',
  TR: 'table-row', TD: 'table-cell', TH: 'table-cell', TABLE: 'table', TBODY: 'table-row-group',
  THEAD: 'table-header-group', TFOOT: 'table-footer-group', LI: 'list-item',
  DIV: 'block', P: 'block', UL: 'block', OL: 'block', CENTER: 'block', H3: 'block', H4: 'block'
}
function defaultDisplay (nodeName) {
  if (!displayCache[nodeName]) {
    var el = document.createElement(nodeName)
    document.body.appendChild(el)
    var d = window.getComputedStyle(el).display
    document.body.removeChild(el)
    displayCache[nodeName] = (!d || d === 'none') ? 'block' : d
  }
  return displayCache[nodeName]
}
function showEl (el) {
  if (el.style.display === 'none') el.style.display = ''
  if (window.getComputedStyle(el).display === 'none') el.style.display = defaultDisplay(el.nodeName)
}
function hideEl (el) {
  if (el.style.display !== 'none') el.style.display = 'none'
}
function isHiddenOwn (el) {
  return el.style.display === 'none' || window.getComputedStyle(el).display === 'none'
}
function isHiddenWithin (el, stop) {
  for (var n = el; n && n !== stop; n = n.parentElement) {
    if (isHiddenOwn(n)) return true
  }
  return false
}

export default {
  name: 'DoacFollowupTool',

  props: {
    /** 'en' or 'fr' */
    language: { type: String, default: 'en' },
    /**
     * Where the files that used to live on the vivomap server are hosted (product monographs and
     * the drug-interaction charts), mirroring the original paths: <base>/assets/programs/116/…
     */
    assetBaseUrl: { type: String, default: '/clinical_tools/vivomap' },
    /**
     * Optional async function({ html, language, css }) => url, replacing the old server-side
     * html2pdf endpoint. Without it the summary opens in a print window ("Save as PDF").
     */
    pdfHandler: { type: Function, default: null }
  },

  data: function () {
    return {
      lang: this.language === 'fr' ? 'fr' : 'en',
      radio: blankRadio(),
      checks: blankChecks(),
      tables: blankTables(),
      text: blank(TEXT_FIELDS),
      committed: blank(TEXT_FIELDS), // values accepted on blur / change
      popup1: false, // P['1']: Drug-Food Interactions
      chadsOpen: false, // toggleCHADS2Calc()
      hbOpen: false, // toggleHBCalc()
      chadsScored: false, // q_6536() has run: its footer shows the total
      hbScored: false, // q_6680() has run
      pdfUrl: '',
      modal: { open: false, messageEn: '', messageFr: '' }
    }
  },

  computed: {
    questions: function () { return CONTENT.questions },
    layout: function () { return LAYOUT },
    assetBase: function () { return this.assetBaseUrl.replace(/\/+$/, '') },
    widgetHtml: function () {
      var base = this.assetBase
      return {
        2: CONTENT.widgets['2'],
        Summary: CONTENT.widgets.Summary.split('%%ASSET_BASE%%').join(base)
      }
    },

    Q4: function () { return this.radio[6444] },
    ageNum: function () { return parseInt(this.committed.q_6441_1, 10) },

    /* CalcVars(): Cockcroft-Gault creatinine clearance (µmol/L) */
    vars: function () {
      var c = this.committed
      var Age = parseFloat(c.q_6441_1)
      var WKG = parseFloat(c.q_6442_1)
      var CMG = parseFloat(c.q_6461_1_subtext)
      var CrCl = null
      if (!!Age && !!this.radio[6443] && !!WKG && !!CMG) {
        var k = this.radio[6443] === '2' ? 0.85 : 1
        CrCl = (((140 - parseInt(Age, 10)) * WKG * k) / (CMG * (72 / 88.4))).toFixed(1)
      }
      return {
        CrCl: CrCl,
        CMG: isNaN(CMG) ? null : CMG,
        CHADS2: this.chadsTotal,
        HAS_BLED: this.hbTotal
      }
    },

    // q_6536(): CHADS2 = C + H + A + D + 2×S
    chadsTotal: function () {
      var a = this.checks[6536]
      return (has(a, 1) ? 1 : 0) + (has(a, 2) ? 1 : 0) + (has(a, 3) ? 1 : 0) + (has(a, 4) ? 1 : 0) + (has(a, 5) ? 2 : 0)
    },
    // q_6680(): one point per item
    hbTotal: function () { return this.checks[6680].length },

    egfr: function () { return this.committed.q_6462_1_subtext },

    vis: function () {
      var Q4 = this.Q4
      var egfr = this.egfr
      return {
        6441: true, 6442: true, 6443: true, 6444: true,
        6445: Q4 === '1', 6447: Q4 === '2', 6448: Q4 === '3', 6449: Q4 === '4', // dose and frequency
        6451: true, 6536: this.chadsOpen,
        6452: true, 6453: true, 6677: true, 6454: true, 6455: true, 6456: true,
        6535: this.popup1, // Drug-Food Interactions
        6457: true, 6678: true, 6680: this.hbOpen,
        6458: true, 6459: has(this.checks[6458], 3), // hemoglobin when "Drop in hemoglobin"
        6460: true, 6462: true,
        6461: egfr !== '' && parseInt(egfr, 10) < 50, // serum creatinine when eGFR < 50
        6463: true, 6679: true, 6464: true, 6465: true, 6466: true, 6468: true, 6467: true, 6469: true,
        6470: true, 6471: true,
        6472: this.tables[6471][3] === '1', // details when "changes needed? Yes"
        6473: true, 6474: true, 6475: true, 6476: true
      }
    },

    widgetVisible: function () {
      return {
        2: this.vis[6461] && this.committed.q_6461_1 !== '', // P['2'] (CrCl box)
        Summary: true
      }
    },

    hasAnswers: function () {
      var self = this
      return RADIOS.some(function (k) { return self.radio[k] !== null }) ||
        CHECKS.some(function (k) { return self.checks[k].length > 0 }) ||
        TEXT_FIELDS.some(function (k) { return self.text[k] !== '' }) ||
        Object.keys(this.tables).some(function (q) {
          return Object.keys(self.tables[q]).some(function (r) { return self.tables[q][r] !== null })
        }) || this.popup1 || this.chadsOpen || this.hbOpen
    },

    syncKey: function () {
      return JSON.stringify([this.radio, this.checks, this.tables, this.committed, this.vis, this.widgetVisible,
        this.lang, this.pdfUrl, this.assetBase])
    },

    settleKey: function () {
      return JSON.stringify([this.ageNum, this.checks[6536], this.checks[6680]])
    }
  },

  watch: {
    language: function (v) { this.lang = v === 'fr' ? 'fr' : 'en' },

    // hideQuestions(): hiding a question clears its answers
    vis: function (now, before) {
      var self = this
      Object.keys(now).forEach(function (id) {
        if (before && before[id] && !now[id]) self.clearQuestion(id)
      })
    },

    'checks.6536': function () { this.chadsScored = true },
    'checks.6680': function () { this.hbScored = true },

    settleKey: function () { this.settle() },

    syncKey: function () {
      this.pdfUrlIfStale()
      this.$nextTick(this.syncDom)
      this.$emit('change', this.snapshot())
    }
  },

  mounted: function () {
    this.syncDom()
  },

  methods: {
    tr: function (pair) {
      var h = pair ? (pair[this.lang] || pair.en || '') : ''
      return h.indexOf('%%ASSET_BASE%%') === -1 ? h : h.split('%%ASSET_BASE%%').join(this.assetBase)
    },
    langHtml: function (pair) { return langSpans(pair.en, pair.fr) },
    isChoice: function (qid) { var t = this.questions[qid].type; return t === 'radio' || t === 'checkbox' },
    boxClass: function (qid) {
      var type = this.questions[qid].type
      var c = [type === 'textblock' ? 'vm-textblock' : type]
      // mergeQuestions([6451, 6536]) / ([6678, 6680]): calculator joined to its score question
      if ((qid === '6451' && this.chadsOpen) || (qid === '6678' && this.hbOpen)) c.push('vm-merged-top')
      if (qid === '6536' || qid === '6680') c.push('vm-merged-bottom')
      return c
    },
    // radios use row_0.. and checkboxes row1.. in the original markup
    rowClass: function (qid, i) { return this.questions[qid].type === 'radio' ? 'row_' + i : 'row' + (i + 1) },
    // Clicks on the input itself are cancelled (.prevent) so the state alone decides what is
    // checked. Browsers restore a cancelled radio/checkbox to its previous state after the click
    // (after Vue has patched it), so re-apply the state once the click has finished.
    resyncInputs: function () {
      var self = this
      setTimeout(function () {
        if (!self.$el || !self.$el.querySelectorAll) return
        self.$el.querySelectorAll('.vm-questions input[type="radio"], .vm-questions input[type="checkbox"]').forEach(function (el) {
          var m = /^q_(\d+)_(\d+)(?:_(\d+))?$/.exec(el.id)
          if (!m) return
          var want = m[3] ? (self.tables[m[1]][m[2]] === m[3]) : self.isChecked(m[1], m[2])
          if (el.checked !== want) el.checked = want
        })
      }, 0)
    },
    isChecked: function (qid, value) {
      return this.checks[qid] ? has(this.checks[qid], value) : this.radio[qid] === value
    },
    isDisabled: function (qid, value) {
      var nota = NOTA[qid]
      return !!nota && has(this.checks[qid], nota) && value !== nota
    },

    subTextHtml: function (qid) {
      // q_6444(): Q13's sub-text links the interaction chart for the chosen DOAC
      if (qid === '6465') {
        var chart = INTERACTION_CHART[this.Q4]
        if (!chart) return ''
        return '<a href="' + this.assetBase + '/assets/programs/116/' + chart + '.png" target="_blank">' +
          (this.lang === 'fr' ? 'Interactions médicamenteuses pouvant affecter les taux d’AOD' : 'Drug interactions that may affect DOAC drug levels') +
          '</a>'
      }
      return this.tr(this.questions[qid].subText)
    },

    footerHtml: function (qid) {
      var fr = this.lang === 'fr'
      if (qid === '6451') {
        return '<button type="button" class="btn btn-info" data-action="chads-calc">' +
          (fr ? 'Calculateur CHADS<sub>2</sub>' : 'CHADS<sub>2</sub> Calculator') + '</button>'
      }
      if (qid === '6678') {
        return '<button type="button" class="btn btn-info" data-action="hb-calc">' +
          (fr ? 'Calculateur HAS-BLED' : 'HAS-BLED Calculator') + '</button>'
      }
      if (qid === '6536' && this.chadsScored) {
        var c = this.chadsTotal
        return '<span style="font-size: 18px; color: black;">TOTAL: <strong>' + c + '</strong><br><br>' +
          '<button type="button" class="btn btn-success" data-action="use-chads">' +
          (fr ? 'Cliquez pour utiliser la valeur calculée "' + c + '" comme score CHADS2.' : 'Click to use calculated value "' + c + '" as the CHADS2 score.') +
          '</button></span>'
      }
      if (qid === '6680' && this.hbScored) {
        var h = this.hbTotal
        return '<span style="font-size: 18px; color: black;">TOTAL: <strong>' + h + '</strong><br><br>' +
          '<button type="button" class="btn btn-success" data-action="use-hb">' +
          (fr ? 'Cliquez pour utiliser la valeur calculée "' + h + '" comme score HAS-BLED.' : 'Click to use calculated value "' + h + '" as the HAS-BLED score.') +
          '</button></span>'
      }
      return this.tr(this.questions[qid].footer)
    },

    dateMax: function (qid) {
      // q_6461 / q_6462: the measurement can't be in the future
      return (qid === '6461' || qid === '6462' || qid === '6459') ? isoToday() : null
    },
    dateMin: function (qid) {
      // q_6475 / q_6476: next follow-up / bloodwork can't be in the past
      return (qid === '6475' || qid === '6476') ? isoToday() : null
    },

    /** Clears every answer (public). */
    reset: function () {
      this.radio = blankRadio()
      this.checks = blankChecks()
      this.tables = blankTables()
      this.text = blank(TEXT_FIELDS)
      this.committed = blank(TEXT_FIELDS)
      this.popup1 = false
      this.chadsOpen = false
      this.hbOpen = false
      this.pdfUrl = ''
      this.closeModal()
      var self = this
      // the score flags follow the cleared checklists' watchers
      this.$nextTick(function () { self.chadsScored = false; self.hbScored = false })
    },

    snapshot: function () {
      var c = this.committed
      var r = this.radio
      var dose = DOSE_Q[r[6444]]
      return {
        answers: {
          age: c.q_6441_1,
          weightKg: c.q_6442_1,
          weightLb: c.q_6442_2,
          sex: r[6443],
          doac: r[6444],
          dose: dose ? r[dose] : null,
          chads2: c.q_6451_1,
          chads2Calculator: this.checks[6536].slice(),
          healthStatus: this.checks[6453].slice(),
          healthComments: c.q_6677_1,
          missedDoses: r[6455],
          adherenceIssues: r[6456],
          hasBled: c.q_6678_1,
          hasBledCalculator: this.checks[6680].slice(),
          bleedingRisk: this.checks[6458].slice(),
          otherBleeding: c.q_6458_2_subtext,
          hemoglobin: { date: c.q_6459_1, result: c.q_6459_1_subtext },
          egfr: { date: c.q_6462_1, result: c.q_6462_1_subtext },
          serumCreatinine: { date: c.q_6461_1, result: c.q_6461_1_subtext },
          dehydration: r[6463],
          renalComments: c.q_6679_1,
          drugInteractions: this.checks[6465].slice(),
          bloodPressure: { systolic: c.q_6468_1, diastolic: c.q_6468_2, status: r[6467] },
          gaitReferral: r[6469],
          assessment: Object.assign({}, this.tables[6471]),
          therapyChange: c.q_6472_1,
          counselling: Object.assign({}, this.tables[6474]),
          nextFollowUp: c.q_6475_1,
          nextBloodwork: c.q_6476_1
        },
        vars: Object.assign({}, this.vars),
        hasAnswers: this.hasAnswers,
        summaryVisible: this.hasAnswers
      }
    },

    clearFields: function (ids) {
      var self = this
      ids.forEach(function (id) { self.text[id] = ''; self.committed[id] = '' })
    },

    clearQuestion: function (id) {
      switch (id) {
        case '6445': case '6447': case '6448': case '6449': this.radio[id] = null; break
        case '6536': this.checks[6536] = []; break
        case '6680': this.checks[6680] = []; break
        case '6459': this.clearFields(['q_6459_1', 'q_6459_1_subtext']); break
        case '6461': this.clearFields(['q_6461_1', 'q_6461_1_subtext']); break
        case '6472': this.clearFields(['q_6472_1']); break
        case '6535': this.popup1 = false; break
      }
    },

    /* ---------------- answers ---------------- */

    choose: function (qid, value) {
      if (this.checks[qid]) this.toggleCheck(qid, value)
      else this.radio[qid] = value // radioOn()
    },

    // toggleCheck() + notaLock()
    toggleCheck: function (qid, value, quiet) {
      if (this.isDisabled(qid, value)) return
      var opts = this.questions[qid].options
      var current = this.checks[qid]
      var on = !has(current, value)
      var next = opts.map(function (o) { return o.value }).filter(function (v) {
        return v === value ? on : has(current, v)
      })
      if (on && NOTA[qid] === value) next = [value] // "None of the above" clears and locks the others
      this.checks[qid] = next
      var self = this
      opts.forEach(function (o) {
        if (o.subtext && !has(next, o.value)) self.clearFields(['q_' + qid + '_' + o.value + '_subtext'])
      })
      if (on && !quiet) this.focusSubtext(qid, value)
    },

    focusSubtext: function (qid, value) {
      var self = this
      this.$nextTick(function () {
        var r = self.$refs['subtext_' + qid + '_' + value]
        var el = Array.isArray(r) ? r[0] : r
        if (el) el.focus()
      })
    },

    // toggleRadioTable(): one answer per row; clicking the chosen answer again clears it
    toggleTable: function (qid, row, col) {
      var v = String(col)
      this.tables[qid][row] = this.tables[qid][row] === v ? null : v
    },

    /*
     * ageWatchdog(): with a valid age, the calculators' age items follow it
     * (CHADS2 "Age ≥ 75?", HAS-BLED "Age ≥ 65"), whether or not the calculator is open.
     */
    settle: function () {
      var age = this.ageNum
      if (isNaN(age)) return
      if ((age >= 75) !== has(this.checks[6536], 3)) this.toggleCheck('6536', '3', true)
      if ((age >= 65) !== has(this.checks[6680], 7)) this.toggleCheck('6680', '7', true)
    },

    toggleCalc: function (which) {
      if (which === 'chads') this.chadsOpen = !this.chadsOpen
      else this.hbOpen = !this.hbOpen
      // the closed calculator is cleared (hideQuestions); the age item is re-applied
      this.$nextTick(this.settle)
    },

    // updateCHADS2() / updateHB()
    useScore: function (which) {
      var field = which === 'chads' ? 'q_6451_1' : 'q_6678_1'
      this.text[field] = String(which === 'chads' ? this.chadsTotal : this.hbTotal)
      this.onFieldBlur(field)
    },

    /* ---------------- validation (testNumber, surveyLogic checks, alerts) ---------------- */

    // testNumber(): out-of-range or non-numeric values are cleared with a message
    testNumber: function (field, lower, upper, frPrefix) {
      var answer = this.text[field]
      if (answer === '') return false
      if (parseInt(answer, 10) < lower || parseInt(answer, 10) > upper || !isNumeric(answer)) {
        this.text[field] = ''
        this.showMessage(
          'Please enter a value between ' + lower + '-' + upper + '.',
          frPrefix ? frPrefix + ' ' + lower + '-' + upper + '.' : 'Veuillez entrer une valeur entre ' + lower + ' et ' + upper + '.'
        )
        return false
      }
      return true
    },

    // "Only numbers please." (lab results, blood pressure)
    onlyNumbers: function (field) {
      var v = this.text[field]
      if (v === '') return true
      if (isNaN(parseInt(v, 10))) {
        this.text[field] = ''
        this.showMessage('Only numbers please.', 'Seulement des chiffres s\'il vous plaît.')
        return false
      }
      return true
    },

    // surveyLogic() check on number questions
    requireNumeric: function (field) {
      var v = this.text[field]
      if (v !== '' && !(parseInt(v, 10) >= 0 && isNumeric(v))) {
        this.text[field] = ''
        this.showMessage('Please enter a numerical value', 'S\'il vous pla&icirc;t entrer une valeur num&eacute;rique')
        return false
      }
      return true
    },

    commit: function (field) { this.committed[field] = this.text[field] },

    onFieldBlur: function (field) {
      var t = this.text
      switch (field) {
        case 'q_6441_1': // q_6441: age 1–120
          this.testNumber(field, 1, 120)
          break
        case 'q_6442_1': // q_6442_1(): kg 27–227 -> lb
          if (t.q_6442_1 === '') {
            t.q_6442_2 = ''
          } else if (this.testNumber(field, 27, 227)) {
            t.q_6442_2 = (parseFloat(t.q_6442_1) * 2.2).toFixed(0)
          } else {
            t.q_6442_2 = ''
          }
          this.commit('q_6442_2')
          break
        case 'q_6442_2': // q_6442_2(): lb 60–500 -> kg
          if (t.q_6442_2 === '') {
            t.q_6442_1 = ''
          } else if (this.testNumber(field, 60, 500)) {
            t.q_6442_1 = (parseFloat(t.q_6442_2) / 2.2).toFixed(0)
          } else {
            t.q_6442_1 = ''
          }
          this.commit('q_6442_1')
          break
        case 'q_6451_1': // CHADS2 0–6
          this.testNumber(field, 0, 6, 'Veuillez saisir une valeur comprise entre')
          break
        case 'q_6678_1': // HAS-BLED 0–9
          this.testNumber(field, 0, 9, 'Veuillez saisir une valeur comprise entre')
          break
        case 'q_6468_1': case 'q_6468_2': // blood pressure
          if (this.onlyNumbers(field)) this.requireNumeric(field)
          break
        case 'q_6459_1_subtext': case 'q_6461_1_subtext': case 'q_6462_1_subtext': // lab results
          this.onlyNumbers(field)
          break
      }
      this.commit(field)
    },

    onDateChange: function (field) {
      var v = this.text[field]
      var today = isoToday()
      if (v !== '' && (field === 'q_6461_1' || field === 'q_6462_1') && v > today) {
        this.text[field] = ''
        this.showMessage('Please do not select a date after today.', 'Veuillez ne pas sélectionner de date après aujourd\'hui')
      } else if (v !== '' && (field === 'q_6475_1' || field === 'q_6476_1') && v < today) {
        this.text[field] = ''
        this.showMessage('Please do not select a date prior to today.', 'Veuillez ne pas sélectionner une date antérieure à aujourd\'hui.')
      }
      this.commit(field)
      // the datepicker's onClose: no date -> its result box is emptied and disabled
      var result = field + '_subtext'
      if (this.text[field] === '' && Object.prototype.hasOwnProperty.call(this.text, result)) this.clearFields([result])
      if (this.text[field] !== '' && Object.prototype.hasOwnProperty.call(this.text, result)) {
        var self = this
        this.$nextTick(function () {
          var el = self.$el.querySelector('#' + result)
          if (el && !self.text[result]) el.focus()
        })
      }
    },

    showMessage: function (en, fr) {
      this.modal = { open: true, messageEn: en, messageFr: fr || en }
    },
    closeModal: function () {
      this.modal = { open: false, messageEn: '', messageFr: '' }
    },

    onContentClick: function (e) {
      var target = e.target
      if (!target || !target.closest) return
      var el = target.closest('[data-action],a[href="#"]')
      if (!el || !this.$el.contains(el)) return
      e.preventDefault()
      switch (el.getAttribute('data-action')) {
        case 'popup1': this.popup1 = !this.popup1; break
        case 'chads-calc': this.toggleCalc('chads'); break
        case 'hb-calc': this.toggleCalc('hb'); break
        case 'use-chads': this.useScore('chads'); break
        case 'use-hb': this.useScore('hb'); break
        case 'pdf': this.generatePdf(); break
      }
    },

    /* ---------------- summary PDF (makeSummaryPDF) ---------------- */

    pdfUrlIfStale: function () {
      var key = JSON.stringify([this.radio, this.checks, this.tables, this.committed, this.lang])
      if (this._pdfKey !== undefined && this._pdfKey !== key) this.pdfUrl = ''
      this._pdfKey = key
    },

    summaryHtml: function () {
      var langClass = this.lang === 'fr' ? 'french' : 'english'
      var src = this.$el.querySelector('.' + langClass + ' .doacSummary')
      if (!src) return ''
      var clone = src.cloneNode(true)
      var orig = src.querySelectorAll('*')
      var copy = clone.querySelectorAll('*')
      var remove = []
      for (var i = 0; i < orig.length; i++) {
        var o = orig[i]
        var tag = o.nodeName
        if (((tag === 'TR' || tag === 'SPAN' || tag === 'LI' || tag === 'UL' || tag === 'DIV') && isHiddenWithin(o, src)) ||
            o.classList.contains('pdf-hidden') || o.classList.contains('debugging')) {
          remove.push(copy[i])
        }
      }
      remove.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n) })
      return clone.innerHTML
    },

    generatePdf: function () {
      var html = this.summaryHtml()
      var lang = this.lang === 'fr' ? 'french' : 'english'
      this.$emit('summary-pdf', { html: html, language: lang })
      if (this.pdfHandler) {
        var self = this
        Promise.resolve(this.pdfHandler({ html: html, language: lang, css: PRINT_CSS }))
          .then(function (url) { self.pdfUrl = url || ''; self.$nextTick(self.syncDom) })
          .catch(function () { self.pdfUrl = '' })
        return
      }
      var w = window.open('', '_blank')
      if (!w) return
      w.document.open()
      w.document.write('<!doctype html><html><head><meta charset="utf-8"><title>DOAC Follow-up – Summary</title>' +
        '<style>' + PRINT_CSS + '</style></head><body>' + html + '</body></html>')
      w.document.close()
      w.focus()
      setTimeout(function () { w.print() }, 300)
    },

    /* ---------------- SummaryPatientTrigger() / SpecialTriggers() port ---------------- */

    syncDom: function () {
      var root = this.$el
      if (!root || !root.querySelectorAll) return
      var self = this
      var all = function (sel) { return root.querySelectorAll(sel) }
      var show = function (sel) { all(sel).forEach(showEl) }
      var hide = function (sel) { all(sel).forEach(hideEl) }
      var html = function (sel, h) { all(sel).forEach(function (e) { if (e.innerHTML !== h) e.innerHTML = h }) }
      var toggle = function (sel, cond) { if (cond) show(sel); else hide(sel); return cond }
      var c = this.committed
      var r = this.radio
      var vars = this.vars
      var yesNo = function (v) { return v === '1' ? langSpans('Yes', 'Oui') : (v === '2' ? langSpans('No', 'Non') : '') }

      var d = new Date()
      html('.vm-date', MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear())

      // switchPdfButton()
      if (this.pdfUrl) {
        all('#pdf_link').forEach(function (a) { a.setAttribute('href', self.pdfUrl) })
        show('#pdf_link'); hide('.click_button')
      } else {
        all('#pdf_link').forEach(function (a) { a.setAttribute('href', '') })
        hide('#pdf_link'); show('.click_button')
      }

      // getValueAnswer() / getTextareaAnswer()
      var valueAnswer = function (Q, answer, h) {
        if (answer !== '' && answer !== null && answer !== undefined) {
          show('.' + Q + '.trigger'); show('.' + Q + '.line'); html('.' + Q + '.value', h === undefined ? escapeHtml(answer) : h)
        } else {
          hide('.' + Q + '.trigger'); hide('.' + Q + '.line'); html('.' + Q + '.value', '')
        }
      }
      var optionHtml = function (qid, o) {
        var h = langSpans(o.en, o.fr)
        if (o.subtext) {
          var v = c['q_' + qid + '_' + o.value + '_subtext']
          if (v) h += '<span> (' + escapeHtml(v) + ')</span> '
        }
        return h
      }
      // getRadioAnswer()
      var radioAnswer = function (Q, qid) {
        var opts = self.questions[qid].options
        opts.forEach(function (o, i) {
          hide('.' + Q + LABELS[i] + '.trigger'); hide('.' + Q + LABELS[i] + '.line'); html('.' + Q + LABELS[i] + '.value', '')
        })
        var v = r[qid]
        if (v !== null) {
          var idx = parseInt(v, 10) - 1
          show('.' + Q + '.trigger'); show('.' + Q + '.line'); html('.' + Q + '.value', optionHtml(qid, opts[idx]))
          show('.' + Q + LABELS[idx] + '.trigger'); show('.' + Q + LABELS[idx] + '.line')
        } else {
          hide('.' + Q + '.trigger'); hide('.' + Q + '.line'); html('.' + Q + '.value', '')
        }
      }
      // getCheckboxAnswers()
      var checkboxAnswers = function (Q, qid) {
        var opts = self.questions[qid].options
        var answers = self.checks[qid]
        var text = ''
        opts.forEach(function (o, i) {
          var cls = '.' + Q + LABELS[i]
          if (has(answers, o.value)) {
            var option = optionHtml(qid, o)
            text += option + ', '
            show(cls + '.trigger'); show(cls + '.line'); html(cls + '.value', option)
          } else {
            hide(cls + '.trigger'); hide(cls + '.line'); html(cls + '.value', '')
          }
        })
        if (text !== '') text = text.slice(0, text.length - 2)
        toggle('.' + Q + '.line', text !== ''); toggle('.' + Q + '.trigger', text !== '')
        html('.' + Q + '.value', text)
      }

      // patient profile
      valueAnswer('Q1', c.q_6441_1)
      valueAnswer('Q2', c.q_6442_1)
      radioAnswer('Q3', '6443')
      radioAnswer('Q4', '6444')
      radioAnswer('Q4_1', '6445')
      radioAnswer('Q4_3', '6447')
      radioAnswer('Q4_4', '6448')
      radioAnswer('Q4_5', '6449')
      valueAnswer('Q5', c.q_6451_1)
      valueAnswer('Q8_5', c.q_6678_1)
      toggle('.Q4_135.line', r[6445] !== null || r[6447] !== null || r[6448] !== null || r[6449] !== null)

      // health status
      checkboxAnswers('Q6', '6453')
      valueAnswer('Q6_1', c.q_6677_1, multiline(c.q_6677_1))
      toggle('.Q6Q6_1.line', has(this.checks[6453], 1) || has(this.checks[6453], 2) || c.q_6677_1 !== '')

      // adherence
      radioAnswer('Q7', '6455')
      radioAnswer('Q8', '6456')
      toggle('.Q7Q8A.line', r[6455] !== null || r[6456] === '1')

      // bleeding risk
      checkboxAnswers('Q9', '6458')
      valueAnswer('Q9_1', c.q_6459_1_subtext)
      toggle('.Q9Q9_1.line', this.checks[6458].length > 0 || c.q_6459_1_subtext !== '')

      // renal function
      var creat = c.q_6461_1_subtext
      var egfr = c.q_6462_1_subtext
      valueAnswer('Q10', creat)
      html('.CMG.value', vars.CMG === null ? '' : String(vars.CMG))
      html('.CrCl.value', vars.CrCl === null ? '' : String(vars.CrCl))
      toggle('.Q11X.trigger', egfr !== '')
      html('.Q11X.value', escapeHtml(egfr))
      toggle('.EGFRLT50.trigger', egfr !== '' && parseInt(egfr, 10) < 50)
      radioAnswer('Q12', '6463')
      valueAnswer('Q12_1', c.q_6679_1, multiline(c.q_6679_1))
      toggle('.Q10Q11Q12A.line', c.q_6461_1 !== '' || creat !== '' || c.q_6462_1 !== '' || egfr !== '' || r[6463] === '1')

      // drug interactions
      checkboxAnswers('Q13', '6465')
      toggle('.Q13X.line', this.checks[6465].length > 0 && !has(this.checks[6465], 4))

      // examination
      var sys = c.q_6468_1
      var dia = c.q_6468_2
      html('.Q14A.value', escapeHtml(sys))
      html('.Q14B.value', escapeHtml(dia))
      toggle('.Q14.line', sys !== '' || dia !== '')
      radioAnswer('Q14_1', '6467')
      radioAnswer('Q15', '6469')
      toggle('.Q14Q15.line', sys !== '' || dia !== '' || r[6467] !== null || r[6469] !== null)

      // final assessment
      var t16 = this.tables[6471]
      var any16 = false
      for (var i = 1; i <= 3; i++) {
        var a = t16[i]
        if (a) any16 = true
        toggle('.Q16' + LABELS[i - 1] + '.trigger', !!a)
        html('.Q16' + LABELS[i - 1] + '.value', yesNo(a))
      }
      toggle('.Q16.line', any16)
      valueAnswer('Q16_1', c.q_6472_1)

      // education & counselling
      var t17 = this.tables[6474]
      var yes17 = false
      for (var j = 1; j <= 4; j++) {
        if (t17[j] === '1') yes17 = true
        toggle('.Q17' + LABELS[j - 1] + '_C1', t17[j] === '1')
      }
      toggle('.Q17X.trigger', yes17)
      valueAnswer('Q18', c.q_6475_1, escapeHtml(displayDate(c.q_6475_1)))
      valueAnswer('Q19', c.q_6476_1, escapeHtml(displayDate(c.q_6476_1)))
      toggle('.Q17Q18Q19.line', yes17 || c.q_6475_1 !== '' || c.q_6476_1 !== '')
    }
  }
}

var PRINT_CSS = "body{font-family:\"Open Sans\",Helvetica,Arial,sans-serif;font-size:13px;color:#333;margin:20px;}table{width:100%;border-collapse:collapse;}td{padding:5px;vertical-align:top;}h3{font-size:20px;margin:10px 0;}ul{margin:0;padding:0 0 0 20px;}a{color:#428bca;}.summary tr{border-bottom:1px solid #f6f5ff;}.summary .main_header{background-color:#aeb8f7;font-weight:bold;}.summary .sub_header{background-color:#f6f5ff;border-top:2px solid #1d2a75;border-bottom:2px solid #1d2a75;font-size:15px;font-weight:bold;text-align:center;}.vt{vertical-align:top;}.summary .border-bottom{border-bottom:2px solid #1d2a75;}.summary .no-border{border:none;}.summary .bold{font-weight:bold;vertical-align:top;}.summary .withBottomBorder td,.summary .withBottomBorder th{border-bottom:1px solid #1d2a75;}.summary .summary-hidden,.click_button,#pdf_link,#pdf_link_fr{display:none;}.highlight{background-color:yellow;color:black;font-weight:bold;}mark{background:#ff0;}.dont-break{page-break-inside:avoid;break-inside:avoid;}*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}"
</script>

<style>
/* ---- Base (bootstrap 3 + vivomap style.css subset, scoped to the tool) ---- */
.vm-doac {
  font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #333;
  background-color: #fff;
  -webkit-text-size-adjust: 100%;
}
.vm-doac *, .vm-doac *::before, .vm-doac *::after { box-sizing: border-box; }
.vm-doac p { margin: 0 0 10px; }
.vm-doac ul, .vm-doac ol { margin-top: 0; margin-bottom: 10px; }
.vm-doac ul ul, .vm-doac ol ul, .vm-doac ul ol { margin-bottom: 0; }
.vm-doac h3 { font-size: 24px; font-weight: 500; line-height: 1.1; margin: 20px 0 10px; }
.vm-doac h4 { font-size: 18px; font-weight: 500; line-height: 1.1; margin: 10px 0; }
.vm-doac b, .vm-doac strong { font-weight: bold; }
.vm-doac sup { font-size: 75%; line-height: 0; position: relative; vertical-align: baseline; top: -0.5em; }
.vm-doac a { color: #428bca; text-decoration: none; cursor: pointer; }
.vm-doac a:hover, .vm-doac a:focus { color: #2a6496; text-decoration: underline; }
.vm-doac mark { background-color: #ff0; color: #000; padding: 0; }
.vm-doac img { max-width: 100%; height: auto; vertical-align: middle; border: 0; }
.vm-doac table { width: 100%; border-collapse: collapse; border-spacing: 0; background-color: transparent; }
.vm-doac table td { padding: 5px; }
.vm-doac th { padding: 10px; text-align: left; }
.vm-doac label { display: inline-block; max-width: 100%; margin-bottom: 5px; font-weight: normal; }

/* ---- Host-page defenses ----
   The doubled class out-ranks host element resets (Vuetify's `* {margin:0;padding:0}`,
   `.v-application ul/p/a`, bare `ul`/`p` rules) and calculator-page rules such as
   `.calcItem label`, `.groupItem label`, `.calcItem input[type=text]` and `table.summary`,
   while staying below this file's more specific component rules. */
.vm-doac.vm-doac p { margin: 0 0 10px; padding: 0; }
.vm-doac.vm-doac ul, .vm-doac.vm-doac ol { margin: 0 0 10px; padding: 0 0 0 40px; }
.vm-doac.vm-doac ul ul, .vm-doac.vm-doac ol ul, .vm-doac.vm-doac ul ol { margin: 0; }
.vm-doac.vm-doac li { margin: 0; padding: 0; }
.vm-doac.vm-doac a { color: #428bca; font-style: normal; background: none; }
.vm-doac.vm-doac a:hover, .vm-doac.vm-doac a:focus { color: #2a6496; }
.vm-doac.vm-doac label {
  display: inline-block; max-width: 100%; margin: 0 0 5px; padding: 0;
  font-size: inherit; font-weight: normal; vertical-align: baseline;
}
.vm-doac.vm-doac h3 { font-size: 24px; font-weight: 500; line-height: 1.1; margin: 20px 0 10px; }
.vm-doac.vm-doac h4 { font-size: 18px; font-weight: 500; line-height: 1.1; margin: 10px 0; }
.vm-doac.vm-doac hr { margin: 20px 0; border: 0; border-top: 1px solid #eee; }
.vm-doac.vm-doac input[type="text"] {
  position: static; top: auto; right: auto; width: auto; margin: 0; float: none;
}
.vm-doac.vm-doac input[type="radio"], .vm-doac.vm-doac input[type="checkbox"] { margin-left: 0; }
.vm-doac.vm-doac table.summary { display: table; width: 100%; border-collapse: collapse; }
/* Host pages may style bare `table`/`td`/`th` (e.g. `table td { border: 1px solid grey }`).
   Reset at (0,1,0) via :where() so it beats element-only rules but every class rule here still
   wins, then restore the browser default for content tables marked border="1". */
.vm-doac :where(table) { border: 0; }
.vm-doac :where(td, th) { border: 0; vertical-align: inherit; }
.vm-doac :where(table[border]:not([border="0"])) { border: 1px outset grey; }
.vm-doac :where(table[border]:not([border="0"]) td, table[border]:not([border="0"]) th) { border: 1px inset; }
/* single class on purpose: must stay below .summary .border-bottom / .bold / .withBottomBorder td */
.vm-doac table.summary td { border-bottom: none; vertical-align: middle; font-weight: inherit; }

.vm-doac .btn {
  display: inline-block; margin-bottom: 0; font-weight: normal; text-align: center; vertical-align: middle;
  cursor: pointer; background-image: none; border: 1px solid transparent; white-space: nowrap;
  padding: 6px 12px; font-size: 14px; line-height: 20px; border-radius: 4px; user-select: none;
}
.vm-doac .btn:hover, .vm-doac .btn:focus { text-decoration: none; }
.vm-doac.vm-doac .btn-info { color: #fff; background-color: #5bc0de; border-color: #46b8da; }
.vm-doac.vm-doac .btn-info:hover, .vm-doac.vm-doac .btn-info:focus { color: #fff; background-color: #39b3d7; border-color: #269abc; }
.vm-doac.vm-doac .btn-primary { color: #fff; background-color: #428bca; border-color: #357ebd; }
.vm-doac.vm-doac .btn-primary:hover, .vm-doac.vm-doac .btn-primary:focus { color: #fff; background-color: #3071a9; border-color: #285e8e; }

/* ---- Language switching (replaces switchLanguage) ---- */
.vm-doac.vm-lang-en .french { display: none !important; }
.vm-doac.vm-lang-fr .english { display: none !important; }

/* ---- Question boxes (lib/survey/css/style.css + thrombosis-styles.css) ---- */
.vm-doac .questionBox {
  position: relative; margin: 0 auto 25px; background-color: #fff; width: 100%;
  padding: 0; border: none; font-size: 14px;
}
.vm-doac .questionBody { margin-bottom: 8px; font-weight: bold; line-height: 28px; }
.vm-doac .questionBody p { margin: 0; }
.vm-doac .textBlockStyle { color: darkorange; }
.vm-doac .questionOptions {
  position: relative; padding: 10px 0 0 0; margin: 0 20px 0 0;
}
.vm-doac .questionBox ul { padding-left: 0; }
.vm-doac .questionBox ul li {
  margin-left: 0; padding-right: 0; margin-bottom: 7px; list-style: none; width: 100%;
  position: relative; cursor: pointer;
}
.vm-doac .questionBox table { width: 100%; font-size: 14px; }
.vm-doac .questionBox tr { vertical-align: top; }
.vm-doac .questionBox td { padding: 0; }
.vm-doac .questionBox .hoverable li td.last { border-bottom: 1px dotted #eee; }
.vm-doac .questionBox .hoverable li:hover td.last { border-bottom: 1px solid #e0e0e0; }
.vm-doac .questionOptions td label { display: block; padding: 5px; margin-bottom: 0; font-size: 14px; cursor: pointer; }
.vm-doac .questionOptions td.vm-radio-cell { width: 48px; padding: 0 0 0 4px; }

.vm-doac .questionOptions input[type="radio"] {
  -webkit-appearance: none; -moz-appearance: none; appearance: none;
  display: block; width: 23px; height: 23px; margin: 2px 0 0; padding: 0;
  border: 1px solid #1d2a75; border-radius: 20px; background-color: #fff;
  outline: none; cursor: pointer; transition: background-color .2s linear;
}
.vm-doac .questionOptions input[type="radio"]:hover,
.vm-doac .questionBox .hoverable li:hover input[type="radio"] {
  box-shadow: inset 0 0 0 3px #fff; background-color: #f6f5ff;
}
.vm-doac .questionOptions input[type="radio"]:checked,
.vm-doac .questionBox .hoverable li:hover input[type="radio"]:checked {
  box-shadow: inset 0 0 0 3px #fff; background-color: #1d2a75;
}
.vm-doac .questionOptions input[type="radio"]:focus-visible { box-shadow: inset 0 0 0 3px #fff, 0 0 0 2px #aeb8f7; }

/* number questions */
.vm-doac .questionBox.number .questionOptions { padding-left: 0; }
.vm-doac .questionBox.number li { cursor: default; }
.vm-doac .questionBox.number label {
  background-color: #fafafa; margin-bottom: 0; padding: 0;
  border-top-left-radius: 5px; border-top-right-radius: 5px;
}
.vm-doac .questionBox.number label span { display: inline-block; padding: 0 10px; line-height: 30px; }
.vm-doac .questionBox .questionOptions input.maintext {
  display: block; width: 100%; padding: 10px; font-size: 14px; line-height: 20px; color: #333;
  background-color: #fafafa; border: none; border-bottom: 1px solid darkgrey; border-radius: 0 5px 5px;
  font-family: inherit;
}
.vm-doac .questionBox .questionOptions input.maintext:focus,
.vm-doac .questionBox .questionOptions input.subtext:focus { outline: none; border-bottom: 1px solid #1d2a75; }
.vm-doac .questionBox .questionOptions input.subtext {
  margin: 0 0 6px 5px; padding: 6px 10px; font-size: 14px; color: #333; font-family: inherit;
  background-color: #fafafa; border: none; border-bottom: 1px solid darkgrey; border-radius: 0 5px 5px;
  max-width: 100%;
}
.vm-doac .questionBox .questionOptions input.subtext:disabled { opacity: .6; cursor: not-allowed; }

.vm-doac .questionFooter { margin-top: 4px; }

/* inline popups (Popup1, Popup2, Popup6, Popup6_1, Popup6_2) */
.vm-doac .questionBox.popup {
  font-weight: normal; width: calc(100% - 30px); margin: 0 auto 50px; min-height: 100px;
  background-color: #fff; border: none; box-shadow: 0 0 5px 5px whitesmoke; padding: 8px 0;
}
.vm-doac .questionBox.popup .questionBody { margin: 0 10px 8px; font-weight: normal; }
.vm-doac .questionBox.popup .questionBody p { margin: 10px 0; }
.vm-doac .questionBox.popup ul li { list-style: inherit; margin: 0 20px; padding: 0 20px 0 10px; cursor: auto; width: auto; }
.vm-doac .questionBox.popup ul { list-style: disc; }
.vm-doac .questionBox.popup td p { margin: 5px; }

/* ---- Recommendation widgets (loadWidget) ---- */
.vm-doac .widget {
  width: calc(100% - 30px); margin: 0 auto 50px; min-height: 100px; background-color: #fff;
  border: none; padding: 0; box-shadow: 0 0 5px 5px whitesmoke;
}
.vm-doac .vm-widget-body { padding: 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.vm-doac .widget ul li { margin: 0; list-style: disc; cursor: auto; width: auto; }
.vm-doac .widget img { width: auto; max-width: 100%; }
.vm-doac .references { margin-top: 20px; display: block; clear: both; font-size: 12px; }
.vm-doac .highlight { background-color: yellow; color: black; font-weight: bold; }
.vm-doac .bullet-item { display: list-item; list-style-type: circle; list-style-position: inside; }

/* ---- Summary (TCToolBleed_Summary inline styles + thrombosis overrides) ---- */
.vm-doac .summary { font-size: 14px; }
.vm-doac .summary tr { border-bottom: 1px solid #f6f5ff; }
.vm-doac .summary .main_header { background-color: #aeb8f7; font-weight: bold; }
.vm-doac .summary .sub_header {
  background-color: #f6f5ff; border-top: 2px solid #1d2a75; border-bottom: 2px solid #1d2a75;
  text-align: center; font-size: 16px; font-weight: bold;
}
.vm-doac .summary .sub_header td { text-align: center; }
.vm-doac .vt { vertical-align: top; }
.vm-doac .summary .border-bottom { border-bottom: 2px solid #1d2a75; }
.vm-doac .summary .no-border { border: none; }
.vm-doac .summary .bold { font-weight: bold; vertical-align: top; }
.vm-doac .summary ul { margin: 0; padding: 0 0 0 20px; }
.vm-doac .summary .withBottomBorder td, .vm-doac .summary .withBottomBorder th { border-bottom: 1px solid #1d2a75; }
.vm-doac .summary .summary-hidden { display: none; }
.vm-doac .dont-break { page-break-inside: avoid; break-inside: avoid; }
.vm-doac .click_button { cursor: pointer; }

/* ---- Modal (#widget) ---- */
.vm-doac .vm-modal-backdrop {
  position: fixed; inset: 0; z-index: 1050; background: rgba(0, 0, 0, .5);
  overflow-y: auto; padding: 30px 10px;
}
.vm-doac .vm-modal {
  position: relative; width: 100%; max-width: 900px; margin: 0 auto; background: #fff;
  border-radius: 6px 6px 4px 4px; box-shadow: 0 5px 15px rgba(0, 0, 0, .5); overflow: hidden;
}
.vm-doac .vm-modal-header { background-color: #3276b1; color: #fff; padding: 0; min-height: 16px; }
.vm-doac .vm-modal-header h3 { margin: 0; padding: 8px; font-size: 20px; line-height: 1.3; }
.vm-doac .vm-modal-x {
  float: right; margin: 6px 10px 0 0; background: none; border: 0; color: #fff; font-size: 24px;
  line-height: 1; opacity: .8; cursor: pointer;
}
.vm-doac .vm-modal-x:hover { opacity: 1; }
.vm-doac .vm-modal-body { padding: 15px 6px; }
.vm-doac .vm-modal-body .questionBox { width: 100%; margin: 0; box-shadow: none; }
.vm-doac .vm-modal-body .questionBox .questionBody { font-weight: normal; line-height: 20px; }
.vm-doac .vm-modal-footer { text-align: right; }
.vm-doac .vm-modal-footer .btn { border-radius: 0 0 4px 0; }

/* thrombosis-styles.css small-screen rules */
@media only screen and (max-width: 480px) and (min-width: 320px) {
  .vm-doac .widget { max-width: 360px; }
}
@media only screen and (max-width: 340px) {
  .vm-doac .widget { width: 100%; max-width: 280px; }
  .vm-doac .summary td { max-width: 130px; word-wrap: break-word; }
}

/* ---- Checkboxes (same look as the radios, square) ---- */
.vm-doac .questionOptions input[type="checkbox"] {
  -webkit-appearance: none; -moz-appearance: none; appearance: none;
  display: block; width: 23px; height: 23px; margin: 2px 0 0; padding: 0;
  border: 1px solid #1d2a75; border-radius: 3px; background-color: #fff;
  outline: none; cursor: pointer; transition: background-color .2s linear;
}
.vm-doac .questionOptions input[type="checkbox"]:hover,
.vm-doac .questionBox .hoverable li:hover input[type="checkbox"] {
  box-shadow: inset 0 0 0 3px #fff; background-color: #f6f5ff;
}
.vm-doac .questionOptions input[type="checkbox"]:checked,
.vm-doac .questionBox .hoverable li:hover input[type="checkbox"]:checked {
  box-shadow: inset 0 0 0 3px #fff; background-color: #1d2a75;
}
.vm-doac .questionOptions input[type="checkbox"]:focus-visible { box-shadow: inset 0 0 0 3px #fff, 0 0 0 2px #aeb8f7; }
/* options locked by "None" */
.vm-doac .questionBox .hoverable li.vm-disabled { cursor: not-allowed; }
.vm-doac .questionBox .hoverable li.vm-disabled label { color: #999; cursor: not-allowed; }
.vm-doac .questionOptions li.vm-disabled input[type="checkbox"] { border-color: #bbb; background-color: #f2f2f2; cursor: not-allowed; box-shadow: none; }
/* free-text question (comments) */
.vm-doac .questionBox.textbox .questionOptions { padding-left: 0; }
.vm-doac .questionBox.textbox li { cursor: default; }
.vm-doac .questionBox.textbox label { background-color: #fafafa; margin-bottom: 0; padding: 0; }
.vm-doac .questionBox.textbox label span { display: inline-block; padding: 0 10px; line-height: 30px; }


/* ---- DOAC-specific (lib/survey/css/style.css + thrombosis-styles.css) ---- */
.vm-doac .questionHeader { font-size: 14pt; font-weight: bold; color: #000; line-height: 1.4; }
.vm-doac .questionBox.vm-textblock { margin-bottom: 10px; }
.vm-doac .questionBox.vm-textblock .questionHeader { color: darkorange; }
.vm-doac .questionSubText { margin: -4px 0 4px; }
.vm-doac.vm-doac .btn-success { color: #fff; background-color: #5cb85c; border-color: #4cae4c; white-space: normal; }
.vm-doac.vm-doac .btn-success:hover, .vm-doac.vm-doac .btn-success:focus { color: #fff; background-color: #47a447; border-color: #398439; }
.vm-doac .questionFooter .btn-info { margin-top: 6px; }

/* textarea questions ("Other comments") */
.vm-doac .questionBox .questionOptions textarea.maintextarea {
  display: block; width: 100%; height: 120px; padding: 10px; font-size: 14px; line-height: 20px; color: #333;
  font-family: inherit; background-color: #fafafa; border: 1px solid darkgrey; border-radius: 5px; resize: vertical;
}
.vm-doac .questionBox .questionOptions textarea.maintextarea:focus { outline: none; border-color: #1d2a75; }

/* date + result questions (jQuery UI datepicker in the original) */
.vm-doac .questionBox.datePicker li { cursor: default; }
.vm-doac .questionBox.datePicker input.vm-date-input { min-height: 40px; }
.vm-doac .questionBox.datePicker input.vm-result-input { display: block; width: 100%; margin: 8px 0 0; padding: 10px; }
.vm-doac .questionBox.datePicker input.vm-result-input:disabled { display: none; }

/* Yes / No tables (radioTable) */
.vm-doac .questionBox table.ratingTable { width: 100%; }
.vm-doac .questionBox table.ratingTable th { padding: 10px; font-weight: bold; text-align: center; }
.vm-doac .questionBox table.ratingTable th.firstTH { width: 50%; }
.vm-doac .questionBox table.ratingTable td { padding: 0; vertical-align: middle; }
.vm-doac .questionBox table.ratingTable td.vm-row-label label { display: block; padding: 5px; margin: 0; cursor: default; }
.vm-doac .questionBox table.ratingTable td.vm-table-cell { text-align: center; cursor: pointer; width: 25%; }
.vm-doac .questionBox table.ratingTable td.vm-table-cell input[type="radio"] { margin: 2px auto; }
.vm-doac .questionBox table.ratingTable tbody tr:hover td { background-color: #fafafa; }

/* summary rows */
.vm-doac .summary td { vertical-align: top; }
.vm-doac .summary center h3 { margin: 10px 0; }
</style>
